/* El viaje viene de viajes-api. Se guarda una copia en el móvil: sin red se usa la última que llegó. */
import { useEffect, useState } from 'react';
import { noAutorizado } from './sesion.ts';
import type { Viaje } from './tipos.ts';

export const VIAJE = 'japon-2026';
const CLAVE = `tabiji.viaje.${VIAJE}`;

interface Copia { etag: string | null; viaje: Viaje }

/** El viaje de un amigo viene sin los exámenes de David ni sus listas personales: se completa con vacíos
    para que la app no tenga que preguntar a cada paso si existen. */
export function completar(v: Viaje): Viaje {
  v.examenes ??= [];
  v.examenesQueNoHara ??= [];
  v.compras ??= [];
  v.documentos.examenes ??= { clave: 'examenes', titulo: 'Exámenes', version: null, secciones: [] };
  return v;
}

function leerCopia(): Copia | null {
  try {
    const c = JSON.parse(localStorage.getItem(CLAVE) ?? 'null');
    return c ? { ...c, viaje: completar(c.viaje) } : null;
  } catch {
    return null;
  }
}

async function pedir(copia: Copia | null): Promise<Viaje> {
  const r = await fetch(`/api/viajes/${VIAJE}`, { headers: copia?.etag ? { 'If-None-Match': copia.etag } : {} });
  if (r.status === 304 && copia) return copia.viaje;
  if (r.status === 401) { noAutorizado(); throw new Error('Sin sesión'); }
  if (!r.ok) throw new Error(`viajes-api respondió ${r.status}`);
  const viaje = completar((await r.json()) as Viaje);
  try {
    localStorage.setItem(CLAVE, JSON.stringify({ etag: r.headers.get('ETag'), viaje }));
  } catch { /* sin sitio: se sigue sin copia */ }
  return viaje;
}

/** El viaje: al instante desde la copia y, en cuanto responde el servidor, la versión al día.
    Se vuelve a preguntar al volver a la app y cada 15 min con ella delante: en Android la PWA
    vuelve del segundo plano sin recargar y no se enteraría de una reimportación (con el ETag, un 304). */
export function useViaje() {
  const [estado, setEstado] = useState<{ viaje: Viaje | null; error: string | null }>(() => ({ viaje: leerCopia()?.viaje ?? null, error: null }));
  useEffect(() => {
    let vivo = true, pidiendo = false;
    const traer = () => {
      if (pidiendo) return;
      pidiendo = true;
      pedir(leerCopia())
        .then(viaje => { if (vivo) setEstado(e => (e.viaje?.generado === viaje.generado ? e : { viaje, error: null })); })
        .catch((e: Error) => { if (vivo) setEstado(s => ({ ...s, error: s.viaje ? null : e.message })); })
        .finally(() => { pidiendo = false; });
    };
    traer();
    const alVolver = () => { if (document.visibilityState === 'visible') traer(); };
    document.addEventListener('visibilitychange', alVolver);
    const i = setInterval(alVolver, 15 * 60_000);
    return () => { vivo = false; document.removeEventListener('visibilitychange', alVolver); clearInterval(i); };
  }, []);
  return estado;
}
